import { Select } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useSegmentEditor } from '@/hooks/segments/useSegmentEditor'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { OrganisationInterface } from '@/types'

import cn from './SegmentsSelect.module.scss'

type SegmentsSelectPropsType = {
  disabled?: boolean
}

const STATUS_LABELS: Record<string, string> = {
  NEW: 'Новый',
  AVAILABLE: 'Доступный',
  ARCHIVED: 'В архиве',
  SUSPENDED_BY_BASE: 'Приостановлен (нет базы)',
  SUSPENDED_BY_PARTNERSHIP: 'Приостановлен (партнёрство)',
  SUSPENDED_BY_ACCESS_REVOKED: 'Приостановлен (доступ закрыт)',
}

function SegmentsSelect({ disabled = false }: SegmentsSelectPropsType) {
  const [open, setOpen] = useState(false)
  const [currentOrganisation, setCurrentOrganisation] =
    useState<OrganisationInterface | null>(null)

  const selectContainerRef = useRef<HTMLDivElement>(null)
  const leaveTimeout = useRef<NodeJS.Timeout | null>(null)

  const {
    selectedOrganisation,
    organisation,
    segmentDto,
    organisationPermissions,
  } = useUserInfoStore((state) => state)
  const { code } = useParams()

  const canEditSegment = (): boolean => {
    if (!organisationPermissions) return false
    return organisationPermissions.canChangeSegment === true
  }

  const getCurrentOrganisation = (): OrganisationInterface | null => {
    if (
      Object.keys(selectedOrganisation).length &&
      code === String(selectedOrganisation.code)
    ) {
      return selectedOrganisation
    }
    if (
      Object.keys(organisation).length &&
      code === String(organisation.code)
    ) {
      return organisation
    }
    return null
  }

  useEffect(() => {
    const org = getCurrentOrganisation()
    setCurrentOrganisation(org)
  }, [code, selectedOrganisation, organisation])

  const canEdit = canEditSegment()

  const {
    segment,
    segmentModels,
    loadSegments,
    handleChangeSegment: changeSegment,
    contextHolder,
  } = useSegmentEditor({
    organisationId: currentOrganisation?.id,
    organisationName: currentOrganisation?.description,
    initialSegment: segmentDto,
    canEdit,
  })

  const handleChangeSegment = (
    _: { value: number; label: string },
    option?:
      | { value: number; label: string }
      | { value: number; label: string }[]
  ) => {
    if (Array.isArray(option) || !option) return
    changeSegment(option.value)
  }

  const handleMouseEnter = () => {
    if (disabled || !canEdit) return
    clearTimeout(leaveTimeout.current as NodeJS.Timeout)
    loadSegments()
    setOpen(true)
  }

  const handleDropdownVisibleChange = (visible: boolean) => {
    if (visible) loadSegments()
    setOpen(visible)
  }

  const handleMouseLeaveContainer = (e: React.MouseEvent<HTMLDivElement>) => {
    if (leaveTimeout.current) clearTimeout(leaveTimeout.current)
    leaveTimeout.current = setTimeout(() => {
      const relatedTarget = e.relatedTarget
      if (!relatedTarget || !(relatedTarget instanceof Node)) {
        setOpen(false)
        return
      }
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(relatedTarget)
      ) {
        setOpen(false)
      }
    }, 100)
  }

  const handleSelectBlur = () => {
    if (
      selectContainerRef.current &&
      !selectContainerRef.current.matches(':hover')
    ) {
      setOpen(false)
    }
  }

  const isOrgSelected = Object.keys(selectedOrganisation).length > 0
  const isDisabled = disabled || !canEditSegment()

  return (
    <>
      {contextHolder}
      <div
        ref={selectContainerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeaveContainer}
        className={[cn.selectContainer, cn.organisationSettings].join(' ')}
      >
        <Select
          disabled={isDisabled}
          value={{ value: segment.code, label: segment.description }}
          options={segmentModels.map((seg) => ({
            value: seg.code,
            label: seg.description,
            disabled: seg.status !== 'AVAILABLE',
          }))}
          optionRender={(option) => {
            const model = segmentModels.find((s) => s.code === option.value)
            const status = model?.status || ''
            return (
              <div className={cn.optionLabel}>
                <span className={cn.optionLabelName}>{option.label}</span>
                <span
                  className={`${cn.optionStatus} ${cn[status.toLowerCase()]}`}
                >
                  {STATUS_LABELS[status]}
                </span>
              </div>
            )
          }}
          onChange={(_, option) => {
            handleChangeSegment(_, option)
            setOpen(false)
          }}
          onBlur={handleSelectBlur}
          onOpenChange={handleDropdownVisibleChange}
          open={open}
          classNames={{
            popup: {
              root: isOrgSelected
                ? [cn.selectPopup, cn.organisationSelected].join(' ')
                : cn.selectPopup,
            },
          }}
          getPopupContainer={() => selectContainerRef.current || document.body}
        />
      </div>
    </>
  )
}

export default SegmentsSelect
